import {
  BUDGET_EDITOR_GROUPING_SCHEMA_VERSION,
  type BudgetEditorGrouping,
  type BudgetEditorGroupingSection,
  type BudgetEditorGroupingValidationCode,
} from "./budgetEditorGroupingContracts";
import { ItemSubType, ItemType } from "./itemTypes";
import {
  normaliseBudgetEditorItemId,
  normaliseBudgetEditorOrganisationId,
  validateAndNormaliseBudgetEditorGrouping,
} from "./budgetEditorGroupingValidation";

export const BUDGET_EDITOR_GROUPING_PROTOTYPE_LABELS = [
  "Sales",
  "Cost of delivery",
  "Team and people",
  "Operating costs",
  "Assets",
  "Liabilities",
] as const;

export type BudgetEditorGroupingPrototypeLabel =
  (typeof BUDGET_EDITOR_GROUPING_PROTOTYPE_LABELS)[number];

export interface BudgetEditorGroupingStarterAccount {
  accountId: string;
  isBankLoan?: boolean;
  itemSubType?: ItemSubType;
  itemType: ItemType;
  section: BudgetEditorGroupingSection;
}

export interface BudgetEditorGroupingAiAssignment {
  accountId: string;
  confidence: number;
  label: BudgetEditorGroupingPrototypeLabel;
}

export interface BudgetEditorGroupingAiSectionProposal {
  assignments: readonly BudgetEditorGroupingAiAssignment[];
  section: BudgetEditorGroupingSection;
}

export interface BudgetEditorGroupingAiProposal {
  minimumConfidence: number;
  sections: readonly BudgetEditorGroupingAiSectionProposal[];
}

export interface BudgetEditorGroupingStarterInput {
  accounts: readonly BudgetEditorGroupingStarterAccount[];
  aiProposal?: BudgetEditorGroupingAiProposal;
  organisationId: string;
  revision: number;
}

export type BudgetEditorGroupingStarterRefusalCode =
  | BudgetEditorGroupingValidationCode
  | "ACCOUNT_TYPE_SECTION_MISMATCH"
  | "DUPLICATE_AI_ASSIGNMENT"
  | "DUPLICATE_AI_SECTION"
  | "INVALID_ACCOUNT_METADATA"
  | "INVALID_AI_CONFIDENCE"
  | "INVALID_AI_LABEL"
  | "INVALID_AI_PROPOSAL"
  | "INVALID_ITEM_SUBTYPE"
  | "INVALID_ITEM_TYPE"
  | "MISSING_AI_SECTION";

export type BudgetEditorGroupingStarterResult =
  | { ok: true; value: BudgetEditorGrouping }
  | { code: BudgetEditorGroupingStarterRefusalCode; ok: false };

type StarterGroup = {
  id: string;
  name: BudgetEditorGroupingPrototypeLabel;
  section: BudgetEditorGroupingSection;
};

const STARTER_GROUPS = [
  ["3cbf9b4e-3ad8-4d5a-8df2-1cebb86fc001", "Sales", "revenue"],
  ["3cbf9b4e-3ad8-4d5a-8df2-1cebb86fc002", "Cost of delivery", "expenses"],
  ["3cbf9b4e-3ad8-4d5a-8df2-1cebb86fc003", "Team and people", "expenses"],
  ["3cbf9b4e-3ad8-4d5a-8df2-1cebb86fc004", "Operating costs", "expenses"],
  ["3cbf9b4e-3ad8-4d5a-8df2-1cebb86fc005", "Assets", "balance_sheet"],
  ["3cbf9b4e-3ad8-4d5a-8df2-1cebb86fc006", "Liabilities", "balance_sheet"],
].map(([id, name, section]) => ({ id, name, section })) as StarterGroup[];

const SECTION_LABELS: Record<
  BudgetEditorGroupingSection,
  readonly BudgetEditorGroupingPrototypeLabel[]
> = {
  balance_sheet: ["Assets", "Liabilities"],
  expenses: ["Cost of delivery", "Team and people", "Operating costs"],
  revenue: ["Sales"],
};
const SECTIONS = ["revenue", "expenses", "balance_sheet"] as const;
const ITEM_TYPES = [
  ItemType.asset,
  ItemType.equity,
  ItemType.expense,
  ItemType.liability,
  ItemType.revenue,
] as const;
const ACCOUNT_KEYS = [
  "accountId",
  "isBankLoan",
  "itemSubType",
  "itemType",
  "section",
] as const;
const INPUT_KEYS = ["accounts", "aiProposal", "organisationId", "revision"];

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);
const hasOnlyKeys = (
  value: Record<string, unknown>,
  keys: readonly string[],
): boolean => Object.keys(value).every((key) => keys.includes(key));
const refusal = (
  code: BudgetEditorGroupingStarterRefusalCode,
): BudgetEditorGroupingStarterResult => ({ code, ok: false });
const expectedSection = (
  itemType: ItemType,
): BudgetEditorGroupingSection | null => {
  if (itemType === ItemType.revenue) return "revenue";
  if (itemType === ItemType.expense) return "expenses";
  return ITEM_TYPES.includes(itemType as never) ? "balance_sheet" : null;
};

const validateAccounts = (
  accounts: unknown,
  organisationId: string,
):
  | { ok: false; result: BudgetEditorGroupingStarterResult }
  | {
      denominator: Array<{
        accountId: string;
        organisationId: string;
        section: BudgetEditorGroupingSection;
      }>;
      ok: true;
      sectionsByAccount: Map<string, BudgetEditorGroupingSection>;
    } => {
  if (!Array.isArray(accounts)) {
    return { ok: false, result: refusal("INVALID_ACCOUNT_METADATA") };
  }
  const denominator = [];
  const sectionsByAccount = new Map<string, BudgetEditorGroupingSection>();
  for (const raw of accounts) {
    if (!isRecord(raw) || !hasOnlyKeys(raw, ACCOUNT_KEYS)) {
      return { ok: false, result: refusal("INVALID_ACCOUNT_METADATA") };
    }
    const accountId = normaliseBudgetEditorItemId(raw.accountId);
    if (accountId === null) {
      return { ok: false, result: refusal("INVALID_ACCOUNT_ID") };
    }
    if (sectionsByAccount.has(accountId)) {
      return { ok: false, result: refusal("DUPLICATE_ACCOUNT_ID") };
    }
    if (
      typeof raw.itemType !== "number" ||
      !ITEM_TYPES.includes(raw.itemType as never)
    ) {
      return { ok: false, result: refusal("INVALID_ITEM_TYPE") };
    }
    if (expectedSection(raw.itemType) !== raw.section) {
      return { ok: false, result: refusal("ACCOUNT_TYPE_SECTION_MISMATCH") };
    }
    if (
      raw.itemSubType !== undefined &&
      (typeof raw.itemSubType !== "number" ||
        ItemSubType[raw.itemSubType] === undefined)
    ) {
      return { ok: false, result: refusal("INVALID_ITEM_SUBTYPE") };
    }
    if (raw.isBankLoan !== undefined && typeof raw.isBankLoan !== "boolean") {
      return { ok: false, result: refusal("INVALID_ACCOUNT_METADATA") };
    }
    const section = raw.section as BudgetEditorGroupingSection;
    sectionsByAccount.set(accountId, section);
    denominator.push({ accountId, organisationId, section });
  }
  return { denominator, ok: true, sectionsByAccount };
};

/**
 * Validates an AI proposal and converts only confident assignments into
 * presentation metadata. Missing or low-confidence assignments stay in the
 * derived Not yet grouped holding area. This function never invokes a model.
 */
export const buildBudgetEditorGroupingStarter = (
  input: BudgetEditorGroupingStarterInput,
): BudgetEditorGroupingStarterResult => {
  if (!isRecord(input) || !hasOnlyKeys(input, INPUT_KEYS)) {
    return refusal("INVALID_ACCOUNT_METADATA");
  }
  const organisationId = normaliseBudgetEditorOrganisationId(
    input.organisationId,
  );
  if (organisationId === null) return refusal("INVALID_ORGANISATION_ID");
  if (
    !Number.isSafeInteger(input.revision) ||
    input.revision < 0 ||
    input.revision >= Number.MAX_SAFE_INTEGER
  ) {
    return refusal("INVALID_REVISION");
  }
  const accountState = validateAccounts(input.accounts, organisationId);
  if (!accountState.ok) return accountState.result;

  const memberships = new Map<BudgetEditorGroupingPrototypeLabel, string[]>();
  if (input.aiProposal !== undefined) {
    const proposal = input.aiProposal;
    if (
      !isRecord(proposal) ||
      !hasOnlyKeys(proposal, ["minimumConfidence", "sections"]) ||
      !Array.isArray(proposal.sections)
    ) {
      return refusal("INVALID_AI_PROPOSAL");
    }
    if (
      typeof proposal.minimumConfidence !== "number" ||
      !Number.isFinite(proposal.minimumConfidence) ||
      proposal.minimumConfidence < 0 ||
      proposal.minimumConfidence > 1
    ) {
      return refusal("INVALID_AI_CONFIDENCE");
    }
    const seenSections = new Set<BudgetEditorGroupingSection>();
    const seenAssignments = new Set<string>();
    for (const sectionProposal of proposal.sections) {
      if (
        !isRecord(sectionProposal) ||
        !hasOnlyKeys(sectionProposal, ["assignments", "section"]) ||
        !SECTIONS.includes(sectionProposal.section as never) ||
        !Array.isArray(sectionProposal.assignments)
      ) {
        return refusal("INVALID_AI_PROPOSAL");
      }
      const section = sectionProposal.section as BudgetEditorGroupingSection;
      if (seenSections.has(section)) return refusal("DUPLICATE_AI_SECTION");
      seenSections.add(section);
      for (const assignment of sectionProposal.assignments) {
        if (
          !isRecord(assignment) ||
          !hasOnlyKeys(assignment, ["accountId", "confidence", "label"])
        ) {
          return refusal("INVALID_AI_PROPOSAL");
        }
        const accountId = normaliseBudgetEditorItemId(assignment.accountId);
        if (accountId === null) return refusal("INVALID_ACCOUNT_ID");
        if (!accountState.sectionsByAccount.has(accountId)) {
          return refusal("UNKNOWN_ACCOUNT_ID");
        }
        if (seenAssignments.has(accountId))
          return refusal("DUPLICATE_AI_ASSIGNMENT");
        seenAssignments.add(accountId);
        if (accountState.sectionsByAccount.get(accountId) !== section) {
          return refusal("ACCOUNT_SECTION_MISMATCH");
        }
        if (!SECTION_LABELS[section].includes(assignment.label as never)) {
          return refusal("INVALID_AI_LABEL");
        }
        if (
          typeof assignment.confidence !== "number" ||
          !Number.isFinite(assignment.confidence) ||
          assignment.confidence < 0 ||
          assignment.confidence > 1
        ) {
          return refusal("INVALID_AI_CONFIDENCE");
        }
        if (assignment.confidence >= proposal.minimumConfidence) {
          const label = assignment.label as BudgetEditorGroupingPrototypeLabel;
          memberships.set(label, [
            ...(memberships.get(label) ?? []),
            accountId,
          ]);
        }
      }
    }
    if (seenSections.size !== SECTIONS.length)
      return refusal("MISSING_AI_SECTION");
  }

  const sections: BudgetEditorGrouping["sections"] = {
    balance_sheet: { groups: [] },
    expenses: { groups: [] },
    revenue: { groups: [] },
  };
  for (const section of SECTIONS) {
    sections[section].groups = STARTER_GROUPS.filter(
      (group) => group.section === section && memberships.has(group.name),
    ).map((group, order) => ({
      accountIds: [...(memberships.get(group.name) ?? [])].sort(),
      id: group.id,
      name: group.name,
      order,
    }));
  }
  return validateAndNormaliseBudgetEditorGrouping(
    {
      organisationId,
      revision: input.revision,
      schemaVersion: BUDGET_EDITOR_GROUPING_SCHEMA_VERSION,
      sections,
    },
    {
      accountDenominator: accountState.denominator,
      expectedOrganisationId: organisationId,
      expectedRevision: input.revision,
    },
  );
};
